import { NeighbourAlarmsResponse, UnifiedAlarm } from '@hakimo-ui/hakimo/types';
import { createContext, useContext } from 'react';
import { TimelineBounds } from '../../types';

const defaultTimelineBounds: TimelineBounds = {
  alarmGroupStartTime: 0,
  alarmGroupEndTime: 0,
  totalTimelineStartTime: 0,
  totalTimelineEndTime: 0,
};

export interface AlarmGroupPlaybackContextType {
  /**
   * Whether the video / timeline is buffering or not
   */
  isBuffering: boolean;
  /**
   * Timeline bounds of the alarm group.
   * alarmGroupStartTime
   * alarmGroupEndTime
   * totalTimelineStartTime
   * totalTimelineEndTime
   */
  timelineBounds: TimelineBounds;
  /**
   * Gaps in the timeline. These are the gaps in the timeline where no alarm video is available
   */
  timelineGaps: Array<[number, number]>;
  /**
   * Update the alarm load state
   * This is called when the alarm video is ready to be played
   */
  updateAlarmLoadState: (alarmId: string, isReady: boolean) => void;
  /**
   * All combined alarms are alarm group alarms and neighbour alarms
   */
  allCombinedAlarms: UnifiedAlarm[];
  /**
   * Show minor alarms
   */
  showMinorAlarms: boolean;
  /**
   * Toggle the show minor alarms
   */
  toggleShowMinorAlarms: (val: boolean) => void;
  /**
   * Handler for processing neighboring alarms response
   */
  handleNeighbourAlarmsResponse: (data: NeighbourAlarmsResponse) => void;
  /**
   * Reset the timeline to the initial state
   */
  resetTimeline: () => void;
  /**
   * Reported by a frames camera each animation frame: whether it has the decoded
   * image for the current playhead. The clock stalls (seeker holds) while any
   * visible camera is not ready — the video-style buffering gate.
   */
  reportFramesReady: (cameraId: string, ready: boolean) => void;
}

export const AlarmGroupPlaybackContext =
  createContext<AlarmGroupPlaybackContextType>({
    isBuffering: true,
    timelineBounds: defaultTimelineBounds,
    timelineGaps: [],
    updateAlarmLoadState: () => ({}),
    allCombinedAlarms: [],
    showMinorAlarms: true,
    toggleShowMinorAlarms: () => ({}),
    handleNeighbourAlarmsResponse: () => ({}),
    resetTimeline: () => ({}),
    reportFramesReady: () => ({}),
  });

export const useAlarmGroupPlayback = () =>
  useContext(AlarmGroupPlaybackContext);
